import type { TemplateResult } from 'lit';
import { authorize, navigate } from '../../redux/app.ts';
import type { Route } from '../../routes.ts';
import type { UserInfo } from '../../types/UserInfo.d.ts';
import { flattenPermissions } from '../auth/flattenPermissions.ts';
import { locationProvider } from '../locationProvider.ts';
import { clone } from '../object/clone.ts';
import { getQueryParams } from '../query/getQueryParams.ts';
import { toQueryString } from '../query/toQueryString.ts';
import { isOkResponse, radHttp, type RadHttpResponse } from '../rad-http/radHttp.ts';

export type RouteParams = Record<string, string | number | undefined>
export type RouteQuery = Record<string, string | number | boolean>
type ParamGetter = (key: string) => string | number | undefined;
type RouteUses = (params?: RouteParams, query?: RouteQuery, requiredAuth?: boolean) => void;
type RouteBefore = (after: (resolve?: boolean) => void) => void;
type RouteHooks = { before: RouteBefore };

export interface RouteConfig {
  /**
   * The path for the route
   * Can only be excluded if notFound is true.
   */
  route?: string;

  /** Permissions required for the route */
  permissions?: string[];

  /** Loads the view */
  view?: MaybePromiseFunction;

  /** The subtitle to add when on this page */
  title?: string;

  /** Renders the route */
  render?: MaybePromiseFunction<TemplateResult>;

  /** Indicates another route to forward to */
  forward?: Route;

  /** Indicates if this is the route for not found */
  notFound?: boolean;
}

interface RouteObject {
  /** The name of the route */
  pageName: Route;

  /**
   * The route/path of the route
   * undefined if notFound Route returned by resolve()
   */
  path?: string;

  /** The pattern to use when matching the route */
  pattern?: RegExp;

  /** The method called when this route is resolved */
  uses?: RouteUses;

  /** The permissions needed for this route */
  permissions?: string[];

  /** Indicates this is a not found route */
  notFound?: boolean;
}

interface RouteResolved {
  /** The matched Route. */
  route: RouteObject;

  /** The params extracted from resolved. */
  params: RouteParams

  /** The path that was resolved. */
  resolved: string;
}

export class Router {
  /** A Promise that eventually resolves to /me or produces an error. */
  private me?: Promise<RadHttpResponse<UserInfo>>;

  private paramGetters: ParamGetter[] = [];
  private beforeCallback?: RouteBefore;
  private notFoundCallback?: RouteUses;
  private notFoundRoute?: RouteObject;
  private routes: RouteObject[] = [];

  constructor() {
    this.loadMe();

    window.addEventListener('popstate', () => {
      this.resolve();
    });
  }

  /**
   * Navigates to the given path.
   * If fullRedirect, it will do a full page change instead of a history push.
   *
   * path can be undefined to make it easier to use with generate()
   */
  navigate(path: string, fullRedirect: boolean = false) {
    if (!this.beforeCallback) {
      this.doNavigate(path, fullRedirect);

      return;
    }

    this.beforeCallback((resolve = true) => {
      if (resolve) {
        this.doNavigate(path, fullRedirect);
      }
    });
  }

  generate(routeName: Route, params: RouteParams = {}, query: RouteQuery = {}, hash: string = ''): string {
    const route = this.routes.find(({ pageName }) => pageName === routeName);

    if (!route) {
      throw new Error(`Unable to find route ${routeName}.`);
    }

    const paramsToReplace = route.path?.match(/(?<=:)([^/]+)/g) ?? ([] as unknown as RegExpMatchArray);

    const url = paramsToReplace
      .reduce((str, key) => str?.replace(
        new RegExp(`:${key}(/?)`, 'g'),
        `${params[key] ?? this.paramGetters.find(getter => getter(key))?.(key)}$1`
      ) ?? '', route.path);

    const queryParamsString = toQueryString(query);

    return `${url}${queryParamsString}${hash ? `#${hash}` : ''}`;
  }

  /**
   * Resolves the given (or current) path.
   * Returns with the not found route if path is not resolved.
   * @example
   * // To check if the route was not found.
   * resolve("/not/found")
   *  .route.notFound // true
   */
  resolve(path: string = locationProvider.pathname): RouteResolved {
    const route = this.routes.find(({ pattern }) => pattern?.test(path));

    if (!route) {
      this.notFoundCallback?.();

      return {
        route: this.notFoundRoute ?? { pageName: '404-not-found', notFound: true },
        params: {},
        resolved: path
      };
    }

    const { pattern, pageName, uses, permissions = [] } = route;
    const { groups: params = {} } = (pattern ? path.match(pattern) : undefined) ?? {};
    const query = getQueryParams();
    const requiresAuth = Boolean(permissions?.length);

    uses?.(params, query, requiresAuth);

    if (requiresAuth) {
      void this.authorize(pageName, permissions, params?.organization);
    }

    return {
      route: clone(route),
      params,
      resolved: path
    };
  }

  /** Updates the query and/or hash of the current route. */
  update({ query, hash }: { query?: RouteQuery, hash?: string }, dispatchNavigate: boolean = true) {
    let newPath = locationProvider.pathname;

    if (query !== undefined) {
      const queryParams = getQueryParams(locationProvider.search);

      Object.assign(queryParams, query);
      newPath += toQueryString(queryParams);
    } else {
      newPath += locationProvider.search;
    }

    if (hash !== undefined) {
      newPath += (!hash || hash.startsWith('#')) ? hash : `#${hash}`;
    } else {
      newPath += locationProvider.hash;
    }

    history.pushState('', '', newPath);

    if (dispatchNavigate) {
      document.dispatchEvent(new CustomEvent('navigate'));
    }
  }

  /** Registers routes to the router. */
  on(routeConfigs: Record<Route, RouteConfig>): this {
    this.routes = this.buildRoutes(routeConfigs);

    const [notFoundPageName, notFoundRoute] = Object.entries(routeConfigs)
      .find(([_, { notFound }]) => notFound) ?? [];

    if (notFoundRoute) {
      this.notFound(notFoundRoute, notFoundPageName as Route);
    }

    return this;
  }

  notFound(notFoundRoute: RouteConfig, pageName: Route = '404-not-found') {
    this.notFoundRoute = this.buildRoute(pageName, notFoundRoute);
    this.notFoundCallback = this.changePageUses(pageName, notFoundRoute.view);

    return this;
  }

  hooks({ before }: RouteHooks) {
    this.beforeCallback = before;

    return this;
  }

  params(...paramGetters: ParamGetter[]) {
    this.paramGetters = paramGetters;

    return this;
  }

  private loadMe() {
    this.me = radHttp.get('./auth/api/me');

    return this;
  }

  private doNavigate(path: string, fullRedirect = false) {
    if (fullRedirect) {
      locationProvider.href = path;

      return;
    }

    history.pushState('', '', path);
    this.resolve(`/${path}`.replace(/^\/+/, '/'));
    document.dispatchEvent(new CustomEvent('navigate'));
  }

  private async authorize(pageName: Route, permissions: string[], organizationSlug: string) {
    const response = await this.me;

    if (!isOkResponse(response)) {
      void authorize(pageName, false);

      return;
    }

    const userPermissions = flattenPermissions(response.data, organizationSlug);

    void authorize(pageName, permissions.every(p => userPermissions.includes(p)));
  }

  /** Converts RouteConfig objects into intermediate Route objects. */
  private buildRoutes(routeConfigs: Record<Route, RouteConfig>): RouteObject[] {
    return Object.entries(routeConfigs)
      .map(([pageName, route]) => this.buildRoute(pageName as Route, route));
  }

  private buildRoute(pageName: Route, { view, route: path, forward, permissions, notFound }: RouteConfig): RouteObject {
    if (!notFound && !path) {
      throw new Error('A RouteConfig must define a route if it is not the notFound route.');
    }

    return {
      pageName,
      path,
      permissions,
      notFound,
      uses: forward
        ? this.forwardPageUses(pageName, forward)
        : this.changePageUses(pageName, view),
      pattern: new RegExp(path
        ? `^${path
          .replace(/:([^/]+)!/g, '(?<$1>.+)')
          .replace(/:([^/]+)/g, '(?<$1>[^/]+)')
          .replace(/\//g, '\\/')}\\/?$`
        : '$.^') // impossible to match pattern
    };
  }

  /** Creates RouteUses for regular routes. */
  private changePageUses(pageName: Route, loadFile?: MaybePromiseFunction): RouteUses {
    return (params: RouteParams = {}, query: RouteQuery = {}, requiresAuth = false) => {
      void navigate(pageName, loadFile, params, query, requiresAuth);
    };
  }

  /** Creates RouteUses for routes that forward. */
  private forwardPageUses(_: string, forward: Route): RouteUses {
    return (params: RouteParams = {}, query: RouteQuery = {}) => {
      this.navigate(`${this.generate(forward, params)}${toQueryString(query)}`);
    };
  }
}